/* eslint-disable @typescript-eslint/only-throw-error -- TODO */
import { type PaymentMethodResult, type StripeError } from '@stripe/stripe-js';
import { supportCallbackKey } from '../notificationCallbacks';

import ErrorLogger from '@/services/logger/ErrorLogger';

interface OrderError {
  message: string;
  hasBeenHandled: boolean;
}

interface APIErrorResponse extends OrderError {
  code: number;
}

export interface RethrowableStripeError extends StripeError {
  hasBeenHandled: boolean;
}

const isHandledOrderError = (res: unknown): res is OrderError | RethrowableStripeError =>
  typeof res === 'object' && res !== null && 'hasBeenHandled' in res;

export class ErrorPaymentHandler {
  private showNotification: (text: string) => void;
  private showNotificationWithSupportLink: (text: string) => void;
  private cancelSubmit: () => void;

  constructor(showNotification: (text: string) => void, cancelSubmit: () => void) {
    this.showNotification = showNotification;
    this.showNotificationWithSupportLink = (text: string) => {
      showNotification(`${text} [contact support](${supportCallbackKey})`);
    };
    this.cancelSubmit = cancelSubmit;
  }

  rethrowIfRequired = (res: unknown) => {
    if (!isHandledOrderError(res) || !res.hasBeenHandled) {
      return;
    }
    console.log('rethrowing', res);

    throw res;
  };

  catchPlug = (res: OrderError): void => {
    console.log('catch plug', res);
    if (!res.hasBeenHandled) console.error(res);

    this.cancelSubmit();
  };

  handleStripeLoading = (err: StripeError) => {
    console.log(err.message);
    if (err.message === 'Failed to load Stripe.js') {
      this.showNotificationWithSupportLink("Payment elements' loading has been blocked, please refresh the page or ");
      console.error(err);
    } else {
      ErrorLogger.logError(new Error(err.message));
      this.showNotificationWithSupportLink('Something went wrong, please refresh the page or');
    }

    return null;
  };

  throwStripeUnavailable = (): never => {
    throw { message: 'Stripe is not available', hasBeenHandled: true };
  };

  handlePaymentMethodError = (res: PaymentMethodResult): never => {
    this.rethrowIfRequired(res);

    if (res.error) {
      const { decline_code: declineCode } = res.error;
      // most of these codes cannot be encountered on payment method creation, but docs don't tell which of them can
      // so here are all of them :hidethepain:

      switch (declineCode) {
        case 'authentication_required':
        case 'approve_with_id':
        case 'reenter_transaction':
        case 'try_again_later':
          this.showNotification('Please try again or check with your bank regarding the issue');
          break;

        case 'call_issuer':
        case 'do_not_honor':
        case 'do_not_try_again':
        case 'fraudulent':
        case 'generic_decline':
        case 'invalid_account':
        case 'lost_card':
        case 'merchant_blacklist':
        case 'new_account_information_available':
        case 'no_action_taken':
        case 'not_permitted':
        case 'pickup_card':
        case 'restricted_card':
        case 'revocation_of_all_authorizations':
        case 'revocation_of_authorization':
        case 'security_violation':
        case 'service_not_allowed':
        case 'stolen_card':
        case 'stop_payment_order':
        case 'transaction_not_allowed':
          this.showNotification(
            'Payment has been declined by the card issuer, please check with your bank regarding the issue',
          );
          break;

        case 'card_not_supported':
          this.showNotification('This card type is not supported');
          break;

        case 'card_velocity_exceeded':
        case 'insufficient_funds':
        case 'withdrawal_count_limit_exceeded':
          this.showNotification('Insufficient funds');
          break;

        case 'currency_not_supported':
          this.showNotification(
            'The card does not supported the selected currency, please check with your bank regarding the issue',
          );
          break;

        case 'duplicate_transaction':
          this.showNotification(
            'A similar transaction has been submitted recently, please check if you have already completed the payment',
          );
          break;

        case 'expired_card':
          this.showNotification('The card has expired');
          break;

        case 'incorrect_number':
        case 'invalid_number':
          this.showNotification('The card number is incorrect');
          break;

        case 'incorrect_cvc':
        case 'invalid_cvc':
          this.showNotification('The CVC code is incorrect');
          break;

        case 'incorrect_zip':
          this.showNotification('The ZIP code is incorrect');
          break;

        case 'invalid_amount':
          this.showNotification(
            'Charge limit exceeded, please check with your bank if you can complete payments of required amount',
          );
          break;

        case 'invalid_expiry_month':
        case 'invalid_expiry_year':
          this.showNotification('Expiration date is incorrect');
          break;

        case 'issuer_not_available':
          this.showNotification(
            'Issuer bank cannot be reached, please check with your bank regarding their availability',
          );
          break;

        case 'testmode_decline':
          this.showNotification('Test card cannot be used to complete the payment');
          break;

        case 'processing_error':
        case undefined:
        default:
          this.showNotificationWithSupportLink('Something went wrong, please try again later or');
      }
    } else {
      this.showNotificationWithSupportLink('Something went wrong, please try again later or');
    }

    throw { ...res, hasBeenHandled: true };
  };

  handlePaymentIntentError = (res: APIErrorResponse | OrderError): never => {
    this.rethrowIfRequired(res);

    switch (res.message) {
      case 'ErrSubscriptionIsActiveOnAnotherAccount':
        this.showNotification('Subscription exists on another account');
        break;

      case 'PaymentProcessingFailed':
        this.showNotificationWithSupportLink('Payment prococessing failed, please try again later or');
        break;

      case 'InvalidCardDetails':
      case 'PaymentMethodVerificationFailed':
        this.showNotification('Payment information is invalid, please double-check and try again');
        break;

      case 'PaymentGatewayCurrencyIncompatible':
        this.showNotificationWithSupportLink('Currency is not supported, please');
        break;

      case 'PaymentIntentInvalid':
        this.showNotificationWithSupportLink('Payment validation failed, please try again later or');
        break;

      case 'PaymentIntentInvalidAmount':
        this.showNotificationWithSupportLink('Discount coupon application failed, please try again later or');
        break;

      case 'CardDeclined':
        this.showNotificationWithSupportLink(
          'Your card was declined, please contact your bank or try another payment method or',
        );
        break;

      case 'ExpiredCard':
        this.showNotificationWithSupportLink(
          'Your card was declined with "expired card" reason. Please contact your bank or try another payment method or',
        );
        break;

      case 'IncorrectCVC':
        this.showNotificationWithSupportLink(
          'Your card was declined with "incorrect cvc" reason. Please try again with correct information, try another payment method or',
        );
        break;

      case 'IncorrectZip':
        this.showNotificationWithSupportLink(
          'Your card was declined with "incorrect zip code" reason. Please try again with correct information or try another payment method or',
        );
        break;

      case 'AccountAlreadyHaveSubscription':
      case 'subscription aleady exists':
        this.showNotificationWithSupportLink(
          'It seems like there is already an active subscription for this email. Please [log in](https://my.clearvpn.com/enter) or [reset your password](https://my.clearvpn.com/enter/password/restore) to get access to your account. If you have any other issues with your subscription, please.',
        );
        break;

      case 'email is invalid':
        this.showNotification('Email address is invalid');
        break;

      case 'SetApp account':
        this.showNotification(
          'You’ve already got ClearVPN available in Setapp. [Install ClearVPN](https://setapp.com/apps/clearvpn) via Setapp to get started.',
        );
        break;

      default:
        this.showNotificationWithSupportLink('Something went wrong, please try again later or');
        break;
    }

    throw { ...res, hasBeenHandled: true };
  };

  handleErrorCardAction = (res: RethrowableStripeError): never => {
    this.rethrowIfRequired(res);

    switch (res.code) {
      case 'payment_intent_authentication_failure':
      case 'setup_intent_authentication_failure':
        this.showNotification(
          'We are unable to authenticate your payment method. Please choose a different payment method and try again.',
        );
        break;

      case undefined:
      default:
        this.showNotificationWithSupportLink('Something went wrong, please try again later or');
    }

    throw { ...res, hasBeenHandled: true };
  };
}
